import React, { useState } from "react";
import { toast } from "sonner";
import { ExternalLink, Loader2, Lock } from "lucide-react";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

/**
 * Cross-hub handoff button per HUB1_SSO_EXPORT_BUILD.md §A2.
 *
 * Locked state: when the current user's `can_access_ai_office` flag is
 * false (default for non-admins), the button is greyed with a lock icon
 * and a click surfaces a toast telling them to request access from
 * their admin.
 *
 * `variant="nav"`  — compact link, for the top-nav row
 * `variant="card"` — full pill button, for the Employz Intelligence page
 */
export default function AiOfficeLink({ variant = "nav" }) {
  const [busy, setBusy] = useState(false);
  const { user } = useAuth();
  const locked = !user?.can_access_ai_office;

  const open = async () => {
    if (locked) {
      toast.error("Locked — request access from admin");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/link/handoff");
      const url = r.data?.redirect_url;
      if (!url) throw new Error("no redirect_url");
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (e) {
      const detail = e?.response?.data?.detail || "Handoff unavailable.";
      toast.error(detail);
    } finally {
      setBusy(false);
    }
  };

  const Icon = locked ? Lock : busy ? Loader2 : ExternalLink;
  const iconCls = busy ? "w-3.5 h-3.5 animate-spin" : "w-3.5 h-3.5";

  if (variant === "card") {
    return (
      <button
        onClick={open}
        disabled={busy}
        data-testid="open-ai-office-card"
        data-locked={locked ? "true" : "false"}
        title={locked ? "Locked — request access from admin" : undefined}
        className={`inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] px-4 py-2.5 rounded-md transition-colors disabled:opacity-60 ${
          locked
            ? "border border-zinc-800 text-zinc-500 hover:text-amber-300 hover:border-amber-500/30 cursor-not-allowed"
            : "border border-amber-500/40 text-amber-300 hover:bg-amber-500/10"
        }`}
      >
        <Icon className={iconCls} />
        {locked ? "AI Office (locked)" : "Open the AI Office"}
        {!locked && <span className="ml-0.5">↗</span>}
      </button>
    );
  }

  return (
    <button
      onClick={open}
      disabled={busy}
      data-testid="open-ai-office-nav"
      data-locked={locked ? "true" : "false"}
      title={locked ? "Locked — request access from admin" : undefined}
      className={`px-4 py-2 text-sm tracking-tight inline-flex items-center gap-2 transition-colors disabled:opacity-60 ${
        locked
          ? "text-zinc-500 hover:text-amber-300 cursor-not-allowed"
          : "text-amber-300/90 hover:text-amber-200"
      }`}
    >
      <Icon className={iconCls} />
      {locked ? "AI Office" : "Open the AI Office"}
      {!locked && <span className="text-amber-400/70">↗</span>}
    </button>
  );
}
